import React, { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Paper,
  Slide,
} from '@material-ui/core'
import { Button } from '@refera/ui-web'
import Draggable from 'react-draggable'
import { useDispatch } from 'react-redux'

import CustomRadio from '_components/budget/modals/edit-pre-approved-budget-modal/radio'
import { Select } from '_/components/inputs'

import { useToast } from '_/hooks/use-toast'

import useStyles from './styles'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { importBudgetTemplate } from '_/modules/budget/actions'
import { navigate, useLocation, useParams } from '@reach/router'
import { ROUTES } from '_/utils/constants'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const PaperComponent = React.forwardRef((props, ref) => {
  return (
    <Draggable
      handle="#import-budget-template"
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds="parent"
    >
      <Paper {...props} ref={ref} />
    </Draggable>
  )
})

const radioOptions = [
  {
    label: 'Importar de um modelo',
    value: 'importFromBudgetTemplate',
    key: 1,
  },
  {
    label: 'Importar de um chamado',
    value: 'importFromServiceOrder',
    key: 2,
  },
]

const defaultValues = {
  importType: 'importFromServiceOrder',
  selectedServiceOrder: null,
}

export function ImportBudgetTemplateModal({ closeModal, serviceOrderIds }) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { serviceOrderId, budgetId } = useParams()

  const { fetchBudget, setResetForm, setLoading } = useBudgetForm()

  const isCreating = pathname.includes('/novo')

  const methods = useForm({
    defaultValues,
  })

  const { setValue, control, register, handleSubmit, watch } = methods

  const { showToast } = useToast()
  const selectedServiceOrderWatch = watch('selectedServiceOrder')

  const onSuccessImport = useCallback(
    async newBudget => {
      const newBudgetId = newBudget?.id

      showToast({ type: 'success' })

      if (newBudgetId && isCreating) {
        // if is importing and doesn't have a budget
        navigate(`${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${newBudgetId}`)
      } else {
        // already has a budget
        await fetchBudget(budgetId)
        setResetForm(true)
      }
    },
    [showToast, budgetId, serviceOrderId, fetchBudget, isCreating, setResetForm]
  )

  const handleSaveModel = useCallback(
    formData => {
      const { selectedServiceOrder, importType } = formData
      const { id: selectedServiceOrderId } = selectedServiceOrder

      const payload = {
        selectedServiceOrderId,
        importType,
        budgetId,
      }

      setLoading(true)
      dispatch(importBudgetTemplate(serviceOrderId, payload))
        .then(onSuccessImport)
        .catch(error => {
          showToast({
            type: 'error',
            message: formatErrorMessage(error),
          })
        })
        .finally(() => {
          closeModal()
          setLoading(false)
        })
    },
    [showToast, closeModal, budgetId, serviceOrderId, setLoading, onSuccessImport]
  )

  useEffect(() => {
    const { available = undefined } = selectedServiceOrderWatch || {}

    // if the budget of this service order doesn't have the new budget structure
    if (available === false) {
      showToast({
        type: 'error',
        message: 'O chamado indicado não tem o orçamento na nova estrutura.',
      })
      setValue('selectedServiceOrder', null)
    }
  }, [selectedServiceOrderWatch])

  return (
    <Dialog
      open
      maxWidth="xl"
      disableEscapeKeyDown
      onClose={closeModal}
      PaperComponent={PaperComponent}
      TransitionComponent={Transition}
      className={styles.dialog}
    >
      <DialogTitle className={styles.titleBlue} id="import-budget-template">
        Orçamento para importar
      </DialogTitle>
      <DialogContent className={styles.container}>
        <FormProvider {...methods}>
          <form id="import-budget-template-form" className={styles.subcontainer}>
            <Grid>
              {/* <Grid className={styles.inlineLabel}>
              <Typography className={styles.labels}>Opções</Typography>
              <Typography className={styles.required}>*</Typography>
              </Grid> */}
              {/* Hidden but used to set importType as importFromServiceOrder */}
              <Grid className={styles.subjectWrapper}>
                <CustomRadio
                  control={control}
                  name="importType"
                  defaultValue="importFromBudgetTemplate"
                  options={radioOptions}
                  onChange={e => setValue('importType', e.target.value)}
                  register={register}
                />
              </Grid>

              {/* Hidden for now */}
              {/* <Select
              control={control}
              className={styles.select}
              budgetTemplate={budgetTemplates}
              isBudgetTemplateField
              name="template"
              onChange={e => setValue('template', e.target.value)}
              label="Modelo"
              register={register}
              /> */}

              <Select
                label="Chamado"
                name="selectedServiceOrder"
                options={serviceOrderIds}
                getLabel={item => item?.id}
                getValue={item => item}
                labelClasses={styles.selectLabel}
                required
              />
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions className={styles.dialogAction}>
        <Button onClick={closeModal} color="red" variant="secondary" className={styles.button}>
          Cancelar
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="primary"
          className={styles.button}
          onClick={handleSubmit(handleSaveModel)}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
